import { parseAddressComponents } from '@components/forms/address-search-field/parseAddressComponents';
import { Button } from '@components/ui';
import { errorBody } from '@lib/api/http';
import { useProfileQuery, useUpdateAddressesMutation } from '@lib/api/profile';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import ServiceAreaCard from './service-area-card';
import type {
  AddressTextField,
  MutationResponse,
  RadiusOption,
  ServiceAreaRecord,
  ServiceLocationState,
  ServiceProfile,
} from './serviceAreaTypes';


const EMPTY_AREA: ServiceAreaRecord = {
  address1: '',
  city: '',
  state: '',
  zip: '',
  radius: 10,
  isReadonly: false,
  latitude: null,
  longitude: null,
};

const isAddressTextField = (name: string): name is AddressTextField =>
  name === 'city' || name === 'state' || name === 'zip';

const areaSnapshot = (area: ServiceAreaRecord): string =>
  JSON.stringify({
    id: area.id ?? null,
    address1: (area.address1 ?? '').trim(),
    city: (area.city ?? '').trim(),
    state: (area.state ?? '').trim(),
    zip: (area.zip ?? '').trim(),
    radius: Number(area.radius ?? 10),
    latitude: area.latitude ?? null,
    longitude: area.longitude ?? null,
  });

const sameAddresses = (
  current: ServiceAreaRecord[],
  saved: ServiceAreaRecord[]
): boolean =>
  current.length === saved.length &&
  current.every((area, index) => {
    const savedArea = saved[index];
    return savedArea ? areaSnapshot(area) === areaSnapshot(savedArea) : false;
  });

interface ServiceAreaProps {
  embedded?: boolean;
}

const ServiceArea = ({ embedded = false }: ServiceAreaProps): JSX.Element => {
  const { t } = useTranslation();
  const radiusArray: RadiusOption[] = [
    {
      key: '10 Miles',
      value: 10,
    },
    {
      key: '20 Miles',
      value: 20,
    },
    {
      key: '50 Miles',
      value: 50,
    },
  ];
  const location = useLocation();
  const massage = (location.state as ServiceLocationState | null)?.massage;
  const { data: profileResult = {} } = useProfileQuery();
  const userDetails = profileResult as ServiceProfile;
  const updateAddresses = useUpdateAddressesMutation();
  const [serviceAreas, setServiceAreas] = useState<ServiceAreaRecord[]>([]);
  const [savedAreas, setSavedAreas] = useState<ServiceAreaRecord[]>([]);

  useEffect(() => {
    if (massage) {
      toast.error(massage);
    }
  }, [massage]);

  useEffect(() => {
    if (userDetails.data && userDetails.data.areas.length > 0) {
      const areas = userDetails.data.areas.map((area) => {
        return { ...area, isReadonly: true };
      });

      setServiceAreas(areas);
      setSavedAreas(userDetails.data.areas.map((area) => ({ ...area })));
    }
  }, [userDetails]);

  const handleResponse = (data: MutationResponse): void => {
    if (data.status) {
      toast.success(data.message);
    } else {
      toast.error(data.message);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const addresses = serviceAreas.map((area) => ({ ...area }));
    let defaultAddressIndex = 0;
    for (let i = 0; i < addresses.length; i++) {
      const address = addresses[i];
      if (!address) {
        continue;
      }
      if (!address.address1) {
        toast.error(`Empty Addresses are not allowed`);
        return;
      }
      if (!address.latitude || !address.longitude) {
        try {
          const results = await geocodeByAddress(address.address1);
          const latLng = await getLatLng(results[0]);
          address.latitude = latLng.lat;
          address.longitude = latLng.lng;
        } catch (error: unknown) {
          toast.error(
            `${address.address1} is an invalid address`
          );
          return;
        }
      }
      if (address.isDefault && address.isDefault == true) {
        defaultAddressIndex = i;
      }
    }
    const defaultAddress = addresses[defaultAddressIndex];
    if (defaultAddress) {
      defaultAddress.isDefault = true;
    }

    updateAddresses.mutate(addresses, {
      onSuccess: (result) => handleResponse(result as MutationResponse),
      onError: (error: unknown) => handleResponse(errorBody(error)),
    });
  };

  const updateArea = (
    index: number,
    patch: Partial<ServiceAreaRecord>
  ): void => {
    setServiceAreas((areas) =>
      areas.map((area, areaIndex) =>
        areaIndex === index ? { ...area, ...patch } : area
      )
    );
  };

  const handleAddAnotherAddress = (): void => {
    setServiceAreas((areas) => [...areas, { ...EMPTY_AREA }]);
  };

  const handleDeleteAddress = (index: number): void => {
    setServiceAreas((areas) =>
      areas.filter((_, areaIndex) => areaIndex !== index)
    );
  };

  const handleEnableInput = (index: number): void => {
    const current = serviceAreas[index];
    if (!current) return;
    if (current.isReadonly) {
      updateArea(index, { isReadonly: false });
      return;
    }
    const saved = savedAreas.find((area) => area.id === current.id);
    setServiceAreas((areas) =>
      areas.map((area, areaIndex) =>
        areaIndex === index ? { ...(saved ?? area), isReadonly: true } : area
      )
    );
  };

  const handleSelectStreetAddress = async (
    streetAddress: string,
    index: number
  ): Promise<void> => {
    if (!streetAddress) return;
    updateArea(index, { address1: streetAddress });
    try {
      const [place] = await geocodeByAddress(streetAddress);
      const { lat, lng } = await getLatLng(place);
      const { city, stateCode, zip } = parseAddressComponents(place);
      updateArea(index, {
        address1: streetAddress,
        city,
        state: stateCode,
        zip,
        latitude: lat,
        longitude: lng,
      });
    } catch {
      toast.error('Unable to parse selected address');
    }
  };

  const handleFieldChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ): void => {
    const { name, value } = event.target;
    if (isAddressTextField(name)) {
      updateArea(index, { [name]: value });
    } else if (name === 'radius') {
      updateArea(index, { radius: value });
    }
  };

  const handleChangeStreetAddress = (address: string, index: number): void => {
    updateArea(index, { address1: address, latitude: null, longitude: null });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {embedded ? null : (
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('nav.serviceAreas')}
        </Typography>
      )}

      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            p: 2,
          }}
        >
          {serviceAreas.map((area, index) => (
            <ServiceAreaCard
              key={area.id || `new-address-${index}`}
              area={area}
              index={index}
              canDelete={serviceAreas.length > 1}
              radiusOptions={radiusArray}
              onToggleEdit={handleEnableInput}
              onDelete={handleDeleteAddress}
              onStreetChange={handleChangeStreetAddress}
              onStreetSelect={(address, areaIndex) => {
                void handleSelectStreetAddress(address, areaIndex);
              }}
              onFieldChange={handleFieldChange}
            />
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleAddAnotherAddress}
            >
              {serviceAreas.length > 0
                ? 'Add another address'
                : 'Add new address'}
            </Button>
          </Box>
        </Box>

        {serviceAreas.length > 0 && (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={
              sameAddresses(serviceAreas, savedAreas) ||
              serviceAreas.some(
                (area) =>
                  !(area.address1 ?? '').trim() ||
                  typeof area.latitude !== 'number' ||
                  typeof area.longitude !== 'number'
              )
            }
            type="button"
            loading={updateAddresses.isPending}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {t('common.update')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ServiceArea;
